import { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Form, Button, Modal, Card, Badge, Row, Col } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';
import { useMediaQuery } from 'react-responsive';

const ParticipationsTable = () => {
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const lastParticipationRef = useRef();
  const [sortConfig, setSortConfig] = useState({ key: 'checkin_time', direction: 'desc' });
  const observer = useRef(); // observer 用於監聽滾動事件
  const { user } = useAuth(); // 取得使用者資訊
  const location = useLocation(); // 用來獲取當前路由的查詢參數
  const queryParams = new URLSearchParams(location.search);
  const isSelf = queryParams.get('self') === 'true'; // 如果路由中有 `self=true`，顯示自己的活動記錄
  
  // 刪除相關狀態
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [canDelete, setCanDelete] = useState(false);

  // 響應式判斷：1200px 以下顯示手機版卡片視圖（對應 lg 斷點）
  const isMobile = useMediaQuery({ maxWidth: 1200 });

  useEffect(() => {
    // 初始化數據
    fetchParticipations(1, sortConfig.key, sortConfig.direction, filter);
    // 檢查刪除權限
    checkDeletePermission();
  }, [filter, sortConfig, isSelf]); // 添加 `isSelf` 作為依賴

  useEffect(() => {
    // 滾動監聽邏輯
    if (loading || !hasMore) return;
    if (observer.current) observer.current.disconnect(); // 防止重複觀察

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1); // 滾動到底部後加載下一頁
      }
    });

    if (lastParticipationRef.current) {
      observer.current.observe(lastParticipationRef.current); // 監聽最後一個參考元素
    }
  }, [loading, hasMore]);

  useEffect(() => {
    if (page > 1) {
      fetchParticipations(page, sortConfig.key, sortConfig.direction, filter); // 分頁加載
    }
  }, [page]);
  //console.log(user); // 確認 user 是否正確包含 e_id

  const fetchParticipations = async (page, sortKey = 'checkin_time', sortDirection = 'desc', filter = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/participations', {
        params: { filter, page, sortKey, sortDirection, self: isSelf, eid: user?.e_id }, // 傳遞使用者的 ID
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setParticipations((prevParticipations) => [...prevParticipations, ...response.data.participations]);
      setHasMore(response.data.participations.length > 0); // 確定是否還有更多數據
    } catch (error) {
      console.error('Error fetching participations:', error);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setParticipations([]); // 清空當前數據
    setPage(1); // 重置頁碼
  };

  // 檢查刪除權限
  const checkDeletePermission = () => {
    if (!user) return;
    
    // 管理員 (e_id = 80) 可以刪除所有記錄
    if (isSuperAdmin(user.e_id)) {
      setCanDelete(true);
      return;
    }
    
    // 秘書 (e_position = 17) 可以刪除所有記錄
    if (user.e_position === 17) {
      setCanDelete(true);
      return;
    }
    
    // 一般用戶在任何情況下都不能刪除記錄
    setCanDelete(false);
  };

  // 處理刪除按鈕點擊
  const handleDeleteClick = (participation) => {
    setRecordToDelete(participation);
    setShowDeleteModal(true);
  };

  // 確認刪除
  const confirmDelete = async () => {
    if (!recordToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/participations/${recordToDelete.record_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // 從列表中移除已刪除的記錄
      setParticipations(prev => 
        prev.filter(p => p.record_id !== recordToDelete.record_id)
      );
      
      setShowDeleteModal(false);
      setRecordToDelete(null);
      
      alert('記錄刪除成功');
    } catch (error) {
      console.error('刪除記錄失敗:', error);
      const errorMessage = error.response?.data?.message || '刪除失敗，請稍後再試';
      alert(errorMessage);
    }
  };

  // 取消刪除
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  // 日期時間格式化函數
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString;
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 手機版卡片視圖組件
  const MobileCardView = () => (
    <div className="row g-3">
      {participations.map((participation, index) => (
        <div
          key={participation.record_id}
          className="col-12"
          ref={index === participations.length - 1 ? lastParticipationRef : null}
        >
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <Badge bg="primary" className="me-2">#{participation.record_id}</Badge>
                  <Badge bg="secondary">{participation.store_name}</Badge>
                </div>
                {canDelete && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(participation)}
                  >
                    刪除
                  </Button>
                )}
              </div>

              <Card.Title className="h5 mb-3">{participation.e_name}</Card.Title>

              <Row className="g-2">
                <Col xs={12}>
                  <div className="text-muted small">活動名稱</div>
                  <div className="fw-bold">{participation.a_name || '-'}</div>
                </Col>
                <Col xs={6}>
                  <div className="text-muted small">報名日期</div>
                  <div className="fw-bold">{formatDate(participation.workDate)}</div>
                </Col>
                <Col xs={6}>
                  <div className="text-muted small">時間記錄</div>
                  <div className="fw-bold">{formatDateTime(participation.cardTime)}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      ))}
      {loading && (
        <div className="col-12">
          <Card className="bg-light">
            <Card.Body className="text-center py-4">
              <div className="spinner-border text-primary me-2" role="status">
                <span className="visually-hidden">載入中...</span>
              </div>
              <span>載入中...</span>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );

  // 桌面版表格視圖組件
  const DesktopTableView = () => (
    <div className="table-responsive">
      <Table striped bordered hover variant="dark" className="table-striped table-dark">
        <thead>
          <tr>
            <th onClick={() => handleSort('store_name')} style={{ cursor: 'pointer' }}>
              社別 {sortConfig.key === 'store_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('e_name')} style={{ cursor: 'pointer' }}>
              姓名 {sortConfig.key === 'e_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('a_name')} style={{ cursor: 'pointer' }}>
              活動名稱 {sortConfig.key === 'a_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('workDate')} style={{ cursor: 'pointer' }}>
              報名日期 {sortConfig.key === 'workDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('cardTime')} style={{ cursor: 'pointer' }}>
              時間記錄 {sortConfig.key === 'cardTime' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            {canDelete && <th>操作</th>}
          </tr>
        </thead>
        <tbody>
          {participations.map((participation, index) => (
            <tr
              key={participation.record_id}
              ref={participations.length === index + 1 ? lastParticipationRef : null}
            >
              <td>{participation.store_name}</td>
              <td>{participation.e_name}</td>
              <td>{participation.a_name}</td>
              <td>{formatDate(participation.workDate)}</td>
              <td>{formatDateTime(participation.cardTime)}</td>
              {canDelete && (
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(participation)}
                  >
                    刪除
                  </Button>
                </td>
              )}
            </tr>
          ))}
          {loading && (
            <tr>
              <td colSpan={canDelete ? "6" : "5"} className="text-center py-4">
                <div className="spinner-border text-light me-2" role="status">
                  <span className="visually-hidden">載入中...</span>
                </div>
                <span>載入中...</span>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <div className="container-fluid mt-4">
      {/* 搜尋框 - 響應式調整 */}
      <div className="mb-4">
        <Form.Control
          type="text"
          placeholder="搜尋參與記錄..."
          value={filter}
          onChange={(e) => {
            setParticipations([]);
            setPage(1);
            setFilter(e.target.value);
          }}
          className={isMobile ? 'form-control-lg' : ''}
          style={{ maxWidth: isMobile ? '100%' : '400px' }}
        />
      </div>

      {/* 響應式視圖切換 */}
      {isMobile ? <MobileCardView /> : <DesktopTableView />}

      {/* 無更多數據提示 */}
      {!hasMore && !loading && participations.length > 0 && (
        <div className="text-center mt-4">
          <Card className="bg-light">
            <Card.Body>
              <p className="mb-0 text-muted">
                {isMobile ? '📋 ' : ''}已顯示全部 {participations.length} 筆資料
              </p>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* 空狀態提示 */}
      {!loading && participations.length === 0 && (
        <div className="text-center mt-5">
          <Card className="bg-light">
            <Card.Body className="py-5">
              <div className="display-1 mb-3">📝</div>
              <h5 className="text-muted">查無參與記錄</h5>
              <p className="text-muted small">請嘗試調整搜尋條件</p>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* 刪除確認對話框 */}
      <Modal show={showDeleteModal} onHide={cancelDelete}>
        <Modal.Header closeButton>
          <Modal.Title>確認刪除</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {recordToDelete && (
            <div>
              <p>您確定要刪除以下簽到記錄嗎？</p>
              <div className="mt-3">
                <strong>社友：</strong>{recordToDelete.e_name}<br/>
                <strong>活動：</strong>{recordToDelete.a_name}<br/>
                <strong>時間：</strong>{recordToDelete.cardTime ? new Date(recordToDelete.cardTime).toLocaleString('zh-TW') : ''}
              </div>
              <div className="mt-3 text-danger">
                <small>此操作無法復原，請謹慎操作。</small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            取消
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            確認刪除
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ParticipationsTable;
